import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { Sparkles, X, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

export default function RfpDraftModal({ open, onClose, onApply }) {
  const [brief, setBrief] = useState("");
  const [title, setTitle] = useState("");
  const [operator, setOperator] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!brief.trim()) { toast.error("Scope brief required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/draft-rfp", {
        title: title || "Untitled Bid",
        operator: operator || "Operator",
        scope_brief: brief,
      });
      setDraft(data);
    } catch { toast.error("Could not generate draft"); }
    finally { setLoading(false); }
  };

  const apply = () => {
    onApply?.({
      title: draft.title || title,
      operator: draft.operator || operator,
      line_items: draft.line_items || [],
      scope_summary: draft.scope_summary,
    });
    onClose();
    // reset
    setBrief(""); setTitle(""); setOperator(""); setDraft(null);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/70 z-40" />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="tray p-6 max-w-2xl w-full pointer-events-auto max-h-[90vh] overflow-y-auto" data-testid="rfp-draft-modal">
              <div className="flex justify-between items-start mb-5">
                <div className="flex items-center gap-3">
                  <Wand2 className="w-5 h-5 text-amber-500" />
                  <div>
                    <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">AI Draft Mode</div>
                    <div className="font-display text-2xl">Generate RFP from a Brief</div>
                    <div className="text-xs text-text-secondary mt-0.5">Describe the project — AI drafts line items across all 5 categories.</div>
                  </div>
                </div>
                <button onClick={onClose} data-testid="draft-close-btn" className="text-text-secondary hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Bid title"
                  data-testid="draft-title-input"
                  className="tray-inset px-3 py-2 bg-transparent text-sm outline-none font-mono-data focus:ring-1 focus:ring-amber-500" />
                <input value={operator} onChange={e => setOperator(e.target.value)} placeholder="Operator"
                  data-testid="draft-operator-input"
                  className="tray-inset px-3 py-2 bg-transparent text-sm outline-none font-mono-data focus:ring-1 focus:ring-amber-500" />
              </div>
              <textarea
                value={brief}
                onChange={e => setBrief(e.target.value)}
                placeholder="e.g. Build 4 miles of 8-inch sour-service piping with 6 metering stations at Marcellus shale pad. 90 days, NACE MR0175 compliant."
                rows={4}
                data-testid="draft-brief-input"
                className="tray-inset w-full px-3 py-2 bg-transparent text-sm outline-none font-ui focus:ring-1 focus:ring-amber-500 mb-4"
              />

              {!draft && (
                <button onClick={generate} disabled={loading || !brief.trim()}
                  data-testid="draft-generate-btn"
                  className="btn-tactile btn-amber w-full inline-flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {loading ? "Drafting line items..." : "Generate RFP Draft"}
                </button>
              )}

              {draft && (
                <div className="space-y-3" data-testid="draft-preview">
                  {draft.scope_summary && (
                    <div className="tray-inset p-3">
                      <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-1">Scope Summary</div>
                      <div className="text-sm">{draft.scope_summary}</div>
                    </div>
                  )}
                  <div className="tray-inset p-3 max-h-64 overflow-y-auto">
                    <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-2">
                      Line items ({draft.line_items?.length || 0})
                    </div>
                    {(draft.line_items || []).map((li, i) => (
                      <div key={i} className="flex items-baseline gap-2 py-1 border-b border-white/5 last:border-0 text-xs font-mono-data">
                        <span className="text-amber-500/80 w-20 shrink-0">{li.category}</span>
                        <span className="flex-1 text-white truncate">{li.description}</span>
                        <span className="text-text-secondary w-20 text-right">{li.quantity} {li.unit}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={generate} disabled={loading} data-testid="draft-regen-btn" className="btn-tactile flex-1 inline-flex items-center justify-center gap-2 text-xs">
                      <Sparkles className="w-3.5 h-3.5" /> Regenerate
                    </button>
                    <button onClick={apply} data-testid="draft-apply-btn" className="btn-tactile btn-amber flex-1">
                      Use this draft →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
